import React, { useState, useEffect } from 'react';
import { X, Sparkles, CheckCircle, AlertCircle, Loader2, RefreshCw, Image as ImageIcon, Download, Maximize2, Minus, Copy } from 'lucide-react';

/**
 * Snackbar queue for AI image generation jobs (and the optional background
 * removal step that follows). Completed jobs show a preview of the generated
 * image plus actions to re-apply it, regenerate, download, or view full screen.
 *
 * Job shape:
 *   { id, queueId, status, progress, phase, filename, elementId,
 *     output_url, original_url, bgRemoved, error, params }
 *   status: 'starting' | 'processing' | 'completed' | 'failed'
 *   phase:  'generating' | 'removing-bg'
 *   queueId:      the AI Queue job id from the workflow
 *   original_url: the generated image BEFORE background removal
 *   output_url:   the final image (cut-out when bgRemoved, else == original_url)
 */
const AIImageGenerationSnackbar = ({
  jobs = [],
  onCancel,
  onDismiss,
  onReapply,
  onRegenerate,
}) => {
  const [visibleJobs, setVisibleJobs] = useState([]);
  // Per-card minimized state: a Set of minimized job ids.
  const [minimizedIds, setMinimizedIds] = useState(() => new Set());
  // { url, label } for the full-screen lightbox, or null.
  const [lightbox, setLightbox] = useState(null);

  const toggleMinimized = (id) => {
    setMinimizedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    setVisibleJobs(jobs.filter(job =>
      ['starting', 'processing', 'completed', 'failed'].includes(job.status)
    ));
  }, [jobs]);

  // Esc closes the lightbox.
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  const statusMeta = (job) => {
    switch (job.status) {
      case 'completed':
        return { icon: <CheckCircle className="w-4 h-4 text-emerald-500" />, text: job.bgRemoved ? 'Applied · background removed' : 'Generated & applied', tint: 'from-emerald-50 to-white border-emerald-200' };
      case 'failed':
        return { icon: <AlertCircle className="w-4 h-4 text-rose-500" />, text: job.error || 'Generation failed', tint: 'from-rose-50 to-white border-rose-200' };
      case 'processing':
        return {
          icon: <Loader2 className="w-4 h-4 text-indigo-500 animate-spin" />,
          text: job.phase === 'removing-bg' ? 'Removing background…' : 'Generating image…',
          tint: 'from-indigo-50 to-white border-indigo-200',
        };
      default:
        return { icon: <Loader2 className="w-4 h-4 text-indigo-500 animate-spin" />, text: 'Starting…', tint: 'from-indigo-50 to-white border-indigo-200' };
    }
  };

  const downloadUrl = (url, filename) => {
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'ai-image.png';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyQueueId = (id) => {
    try { navigator.clipboard?.writeText(String(id)); } catch { /* ignore */ }
  };

  if (visibleJobs.length === 0) return null;

  const renderPreviewImg = (url, label, filename) => (
    <div className="relative flex-1 min-w-0">
      <button
        type="button"
        onClick={() => setLightbox({ url, label })}
        className="group relative w-full aspect-square rounded-lg overflow-hidden bg-[repeating-conic-gradient(#e5e7eb_0%_25%,#f9fafb_0%_50%)] bg-[length:16px_16px] border border-gray-200 flex items-center justify-center cursor-zoom-in"
        title="Click to view full screen"
      >
        <img src={url} alt={label} className="w-full h-full object-contain" />
        <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <Maximize2 className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" />
        </span>
      </button>
      {label && (
        <div className="absolute top-1 left-1 text-[9px] font-medium px-1.5 py-0.5 rounded bg-black/60 text-white">
          {label}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="fixed bottom-4 right-4 z-50 w-80 max-w-[calc(100vw-2rem)] space-y-3">
        {visibleJobs.map((job) => {
          const meta = statusMeta(job);
          const inProgress = job.status === 'starting' || job.status === 'processing';
          const showBoth = job.status === 'completed' && job.bgRemoved && job.original_url && job.output_url && job.original_url !== job.output_url;
          const isMin = minimizedIds.has(job.id);

          // Collapsed card: a single compact row with just the status + expand.
          if (isMin) {
            return (
              <div
                key={job.id}
                className={`bg-gradient-to-b ${meta.tint} border rounded-xl shadow-xl overflow-hidden animate-[slideIn_0.2s_ease-out]`}
              >
                <div className="flex items-center justify-between px-3 py-2">
                  <button
                    type="button"
                    onClick={() => toggleMinimized(job.id)}
                    className="flex items-center gap-2 min-w-0 flex-1 text-left"
                    title="Expand"
                  >
                    <div className="w-6 h-6 rounded-md bg-indigo-600 flex items-center justify-center shrink-0">
                      <Sparkles className="w-3.5 h-3.5 text-white" />
                    </div>
                    <span className="text-xs font-medium text-gray-800 shrink-0">AI Image</span>
                    {meta.icon}
                    <span className="text-[11px] text-gray-500 truncate">{meta.text}</span>
                  </button>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => toggleMinimized(job.id)}
                      className="text-gray-400 hover:text-gray-600 transition-colors p-0.5"
                      title="Expand"
                    >
                      <Maximize2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => onDismiss && onDismiss(job.id)}
                      className="text-gray-400 hover:text-gray-600 transition-colors p-0.5"
                      title="Dismiss"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div
              key={job.id}
              className={`bg-gradient-to-b ${meta.tint} border rounded-xl shadow-xl overflow-hidden backdrop-blur-sm animate-[slideIn_0.2s_ease-out]`}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-3 pt-3">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-7 h-7 rounded-lg bg-indigo-600 flex items-center justify-center shrink-0">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900 leading-tight">AI Image</div>
                    <div className="flex items-center gap-1 text-[11px] text-gray-500 leading-tight">
                      {meta.icon}
                      <span className="truncate">{meta.text}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => toggleMinimized(job.id)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    title="Minimize"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDismiss && onDismiss(job.id)}
                    className="text-gray-400 hover:text-gray-600 transition-colors shrink-0"
                    title="Dismiss"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* AI Queue ID */}
              {job.queueId && (
                <div className="px-3 pt-2">
                  <button
                    type="button"
                    onClick={() => copyQueueId(job.queueId)}
                    className="group inline-flex items-center gap-1 max-w-full text-[10px] font-mono bg-white/70 border border-gray-200 rounded px-1.5 py-0.5 text-gray-600 hover:bg-white transition-colors"
                    title="Copy AI Queue ID"
                  >
                    <span className="text-gray-400 font-sans">Queue ID:</span>
                    <span className="truncate">{job.queueId}</span>
                    <Copy className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                  </button>
                </div>
              )}

              {/* Preview(s) */}
              <div className="px-3 pt-3">
                {showBoth ? (
                  <div className="flex gap-2">
                    {renderPreviewImg(job.original_url, 'Original', job.filename)}
                    {renderPreviewImg(job.output_url, 'Cut-out', job.filename)}
                  </div>
                ) : (
                  <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-[repeating-conic-gradient(#e5e7eb_0%_25%,#f9fafb_0%_50%)] bg-[length:16px_16px] border border-gray-200 flex items-center justify-center">
                    {job.output_url || job.original_url ? (
                      <button
                        type="button"
                        onClick={() => setLightbox({ url: job.output_url || job.original_url, label: job.filename })}
                        className="group w-full h-full flex items-center justify-center cursor-zoom-in"
                        title="Click to view full screen"
                      >
                        <img src={job.output_url || job.original_url} alt={job.filename || 'generated'} className="w-full h-full object-contain" />
                        <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                          <Maximize2 className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" />
                        </span>
                      </button>
                    ) : inProgress ? (
                      <div className="flex flex-col items-center gap-2 text-indigo-400">
                        <Loader2 className="w-6 h-6 animate-spin" />
                        <span className="text-[11px] text-gray-400">
                          {job.phase === 'removing-bg' ? 'Cutting out subject…' : 'Painting pixels…'}
                        </span>
                      </div>
                    ) : (
                      <ImageIcon className="w-7 h-7 text-gray-300" />
                    )}
                  </div>
                )}
              </div>

              {/* Filename / prompt */}
              {(job.filename || job.params?.prompt) && (
                <div className="px-3 pt-2">
                  {job.filename && (
                    <div className="text-[11px] text-gray-500 truncate">📁 {job.filename}</div>
                  )}
                  {job.params?.prompt && (
                    <div className="text-[11px] text-gray-400 truncate" title={job.params.prompt}>
                      “{job.params.prompt}”
                    </div>
                  )}
                </div>
              )}

              {/* Progress bar while working */}
              {inProgress && (
                <div className="px-3 pt-2">
                  <div className="w-full bg-white/70 rounded-full h-1.5 overflow-hidden">
                    <div className="h-full bg-indigo-500 rounded-full animate-[indeterminate_1.2s_ease-in-out_infinite] w-1/3" />
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="px-3 py-3 flex items-center gap-2 flex-wrap">
                {inProgress && (
                  <button
                    onClick={() => onCancel && onCancel(job.id)}
                    className="px-3 py-1.5 text-xs font-medium bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                )}

                {job.status === 'completed' && (
                  <>
                    <button
                      onClick={() => onReapply && onReapply(job)}
                      className="px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1"
                      title="Re-apply this image to the element (restore it if you changed it)"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                      Re-apply
                    </button>
                    <button
                      onClick={() => onRegenerate && onRegenerate(job)}
                      className="px-3 py-1.5 text-xs font-medium bg-white text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors flex items-center gap-1"
                      title="Generate a new image with the same prompt"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                      Regenerate
                    </button>
                    <button
                      onClick={() => downloadUrl(job.output_url || job.original_url, job.filename)}
                      className="px-2 py-1.5 text-xs font-medium bg-white text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1"
                      title="Download"
                    >
                      <Download className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}

                {job.status === 'failed' && (
                  <>
                    <button
                      onClick={() => onRegenerate && onRegenerate(job)}
                      className="px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1"
                      title="Try again"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                      Try again
                    </button>
                    <button
                      onClick={() => onDismiss && onDismiss(job.id)}
                      className="px-3 py-1.5 text-xs font-medium bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Dismiss
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}

        {/* Local keyframes for the entrance + indeterminate bar */}
        <style>{`
          @keyframes slideIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes indeterminate { 0% { transform: translateX(-120%); } 100% { transform: translateX(320%); } }
        `}</style>
      </div>

      {/* Full-screen lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white"
            title="Close (Esc)"
          >
            <X className="w-7 h-7" />
          </button>
          <div className="relative max-w-[92vw] max-h-[88vh]" onClick={(e) => e.stopPropagation()}>
            <div className="rounded-lg overflow-hidden bg-[repeating-conic-gradient(#d1d5db_0%_25%,#f3f4f6_0%_50%)] bg-[length:28px_28px]">
              <img src={lightbox.url} alt={lightbox.label || 'preview'} className="max-w-[92vw] max-h-[80vh] object-contain" />
            </div>
            <div className="mt-3 flex items-center justify-center gap-3">
              {lightbox.label && <span className="text-white/90 text-sm">{lightbox.label}</span>}
              <button
                onClick={() => downloadUrl(lightbox.url, lightbox.label)}
                className="inline-flex items-center gap-1 text-xs bg-white/15 hover:bg-white/25 text-white rounded-lg px-3 py-1.5 transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                Download
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIImageGenerationSnackbar;
